/*
    This file is part of @erc725/erc725.js.
    @erc725/erc725.js is free software: you can redistribute it and/or modify
    it under the terms of the GNU Lesser General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.
    @erc725/erc725.js is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU Lesser General Public License for more details.
    You should have received a copy of the GNU Lesser General Public License
    along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
/**
 * @file providers/web3ProviderWrapper.ts
 * @author Robert McLeod <@robertdavid010>, Fabian Vogelsteller <fabian@lukso.network>
 * @date 2020
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/*
  This file will handle querying the Ethereum web3 rpc based on a given provider
  in accordance with implementation of smart contract interfaces of ERC725
*/
import AbiCoder from 'web3-eth-abi';
import { Method } from '../types/Method';
import { constructJSONRPC, decodeResult } from '../lib/provider-wrapper-utils';
import { ERC725_VERSION, INTERFACE_IDS } from '../lib/constants';
// TS can't get the types from the import...
// @ts-ignore
const abiCoder = AbiCoder;
export class Web3ProviderWrapper {
    constructor(provider) {
        this.type = "WEB3" /* WEB3 */;
        this.provider = provider;
    }
    getOwner(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.callContract(constructJSONRPC(address, Method.OWNER));
            if (result.error) {
                throw result.error;
            }
            return decodeResult(Method.OWNER, result);
        });
    }
    getErc725YVersion(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const isErc725Y = yield this.supportsInterface(address, INTERFACE_IDS.ERC725Y);
            if (isErc725Y) {
                return ERC725_VERSION.ERC725;
            }
            const isErc725YLegacy = yield this.supportsInterface(address, INTERFACE_IDS.ERC725Y_LEGACY);
            return isErc725YLegacy
                ? ERC725_VERSION.ERC725_LEGACY
                : ERC725_VERSION.NOT_ERC725;
        });
    }
    /**
     * https://eips.ethereum.org/EIPS/eip-165
     *
     * @param address the smart contract address
     * @param interfaceId ERC-165 identifier as described here: https://github.com/ERC725Alliance/ERC725/blob/develop/docs/ERC-725.md#specification
     */
    supportsInterface(address, interfaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            return decodeResult(Method.SUPPORTS_INTERFACE, yield this.callContract(constructJSONRPC(address, Method.SUPPORTS_INTERFACE, `${interfaceId}${'00000000000000000000000000000000000000000000000000000000'}`)));
        });
    }
    getData(address, keyHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.getAllData(address, [keyHash]);
            try {
                return result[0].value;
            }
            catch (_a) {
                return null;
            }
        });
    }
    getAllData(address, keyHashes) {
        return __awaiter(this, void 0, void 0, function* () {
            const erc725Version = yield this.getErc725YVersion(address);
            if (erc725Version === ERC725_VERSION.NOT_ERC725) {
                throw new Error(`Contract: ${address} does not support ERC725Y interface.`);
            }
            switch (erc725Version) {
                case ERC725_VERSION.ERC725:
                    return this._getAllData(address, keyHashes);
                case ERC725_VERSION.ERC725_LEGACY:
                    return this._getAllDataLegacy(address, keyHashes);
                default:
                    return [];
            }
        });
    }
    _getAllData(address, keyHashes) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = [
                constructJSONRPC(address, Method.GET_DATA, abiCoder.encodeParameter('bytes32[]', keyHashes)),
            ];
            const results = yield this.callContract(payload);
            const decodedValues = decodeResult(Method.GET_DATA, results[0]);
            return keyHashes.map((key, index) => ({
                key,
                value: decodedValues[index],
            }));
        });
    }
    _getAllDataLegacy(address, keyHashes) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = [];
            // Here we could use `getDataMultiple` instead of sending multiple calls to `getData`
            // But this is already legacy and it won't be used anymore..
            for (let index = 0; index < keyHashes.length; index++) {
                payload.push(constructJSONRPC(address, Method.GET_DATA_LEGACY, keyHashes[index]));
            }
            const results = yield this.callContract(payload);
            return payload.map((payloadCall, index) => ({
                key: keyHashes[index],
                value: decodeResult(Method.GET_DATA_LEGACY, results.find((element) => payloadCall.id === element.id)),
            }));
        });
    }
    callContract(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                // Send old web3 method with callback to resolve promise
                this.provider.send(payload, (e, r) => {
                    if (e) {
                        reject(e);
                    }
                    else {
                        resolve(r);
                    }
                });
            });
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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